import { AssetMasterModule } from "@/store/modules/asset.module";
import { toaster } from "@/utilities/toaster";
import { Machine, assign } from "xstate";
const assetAssignmentMachine = Machine({
    id: "assetAssignmentMachine",
    context: {
        empId: null,
        query: "",
        selectedAssetIds: [],
        returnLetterAssets: [],
    },
    initial: "idle",
    states: {
        idle: {
            always: [{ target: "gettingEmployeeDetails" }],
        },
        gettingEmployeeDetails: {
            invoke: {
                id: "gettingEmployeeDetails",
                src: (ctx, event) => {
                    return AssetMasterModule.GetAllActiveEmployees(ctx.empId);
                },
                onDone: { target: "employeeDetailsLoaded" },
                onError: { target: "serviceUnavailable" },
            },
        },
        employeeDetailsLoaded: {
            on: {
                CHANGE_EMPLOYEE: { actions: "assignEmployeeId", target: "gettingEmployeeAssets" },
                CHANGE_WORK_PLACE: { target: "employeeWorkplace" },
                ASSET_LOOKUP: { actions: "assignEmployeeId", target: "assetLookUp" },
                REMOVE_ASSIGNED_ASSET: { target: "removeAssignedAsset" },
                PRINT_ISSUE_LETTER: { actions: "assignEmployeeId", target: "issueLetter" },
                PRINT_RETURN_LETTER: { actions: "assignEmployeeId", target: "returnLetter" },
            },
        },
        gettingEmployeeAssets: {
            invoke: {
                id: "gettingEmployeeAssets",
                src: (ctx, event) => {
                    return AssetMasterModule.GetEmployeeAssets(ctx.empId);
                },
                onDone: { target: "employeeDetailsLoaded" },
                onError: { target: "employeeDetailsLoaded" },
            },
        },
        employeeWorkplace: {
            invoke: {
                id: "employeeWorkplace",
                src: (ctx, event) => {
                    return AssetMasterModule.UpdateWorkPlace(event.payload);
                },
                onDone: { target: "employeeDetailsLoaded", actions: ["showSuccessMessage"] },
                onError: { target: "employeeDetailsLoaded" },
            },
        },
        assetLookUp: {
            initial: "assignAsset",
            states: {
                assignAsset: {
                    on: {
                        SEARCH: { actions: "assignDataToSearch", target: "debouncingAsset" },
                        ASSIGNING_ASSET: { target: "assigningAsset" },
                        CANCEL_MODAL: { target: "#assetAssignmentMachine.employeeDetailsLoaded" },
                        CLOSE_MODAL: { target: "#assetAssignmentMachine.employeeDetailsLoaded" },
                    },
                },
                debouncingAsset: {
                    on: {
                        SEARCH: { actions: "assignDataToSearch", target: "debouncingAsset" },
                    },
                    after: {
                        1000: {
                            target: "searchAsset",
                        },
                    },
                },
                searchAsset: {
                    invoke: {
                        id: "searchAsset",
                        src: (context, event) => AssetMasterModule.GetAssetsByLookup(context.query),
                        onDone: { target: "assignAsset" },
                        onError: { target: "#assetAssignmentMachine.serviceUnavailable" },
                    },
                },
                assigningAsset: {
                    invoke: {
                        id: "assigningAsset",
                        src: (context, event) => AssetMasterModule.AddAssetAssignment(event.payLoad),
                        onDone: { target: "#assetAssignmentMachine.gettingEmployeeAssets", actions: ["showSuccessMessage"] },
                        onError: { target: "#assetAssignmentMachine.serviceUnavailable" },
                    },
                },
            },
        },
        removeAssignedAsset: {
            tags: "showRemoveAssetUI",
            on: {
                CONFIRM_ASSET_REMOVE: { target: "removingAssignedAsset" },
                CANCEL_MODAL: { target: "employeeDetailsLoaded" },
                CLOSE_MODAL: { target: "employeeDetailsLoaded" },
            },
        },
        removingAssignedAsset: {
            tags: "showRemoveAssetUI",
            invoke: {
                id: "removingAssignedAsset",
                src: (ctx, event) => {
                    return AssetMasterModule.RemoveAssetAssignment(event.assetId);
                },
                onDone: { target: "gettingEmployeeAssets", actions: ["showSuccessMessage"] },
                onError: { target: "employeeDetailsLoaded" },
            },
        },
        issueLetter: {
            initial: "awaitingSelection",
            entry: ["clearSelectedAssetIds"],
            on: {
                CANCEL_MODAL: { target: "employeeDetailsLoaded" },
                CLOSE_MODAL: { target: "employeeDetailsLoaded" },
            },
            states: {
                awaitingSelection: {
                    on: {
                        EXPORT_ISSUE_LETTER: { actions: "assignSelectedAssetIds", target: "exportingLetter" },
                    },
                },
                exportingLetter: {
                    invoke: {
                        id: "exportingLetter",
                        src: (ctx) => {
                            const employee = AssetMasterModule.employeeListing.find((e) => e.employeeId?.toString() === ctx.empId?.toString());
                            const empName = employee?.fullName?.replace(/\s+/g, "_") ?? "Employee";
                            return AssetMasterModule.GenerateAssetIssueLetter({ empId: ctx.empId.toString(), assetIds: ctx.selectedAssetIds })
                                .then((result) => ({ ...result, fileName: `AssetIssueLetter_${empName}.pdf` }));
                        },
                        onDone: { target: "#assetAssignmentMachine.employeeDetailsLoaded", actions: ["openPdfInNewTab"] },
                        onError: { target: "awaitingSelection" },
                    },
                },
            },
        },
        returnLetter: {
            initial: "awaitingSelection",
            entry: ["clearReturnLetterAssets"],
            on: {
                CANCEL_MODAL: { target: "employeeDetailsLoaded" },
                CLOSE_MODAL: { target: "employeeDetailsLoaded" },
            },
            states: {
                awaitingSelection: {
                    on: {
                        EXPORT_RETURN_LETTER: { actions: "assignReturnLetterAssets", target: "exportingLetter" },
                    },
                },
                exportingLetter: {
                    invoke: {
                        id: "exportingReturnLetter",
                        src: (ctx) => {
                            const employee = AssetMasterModule.employeeListing.find((e) => e.employeeId?.toString() === ctx.empId?.toString());
                            const empName = employee?.fullName?.replace(/\s+/g, "_") ?? "Employee";
                            return AssetMasterModule.GenerateAssetReturnLetter({ empId: ctx.empId.toString(), assets: ctx.returnLetterAssets })
                                .then((result) => ({ ...result, fileName: `AssetReturnLetter_${empName}.pdf` }));
                        },
                        onDone: { target: "#assetAssignmentMachine.employeeDetailsLoaded", actions: ["openPdfInNewTab"] },
                        onError: { target: "awaitingSelection" },
                    },
                },
            },
        },
        serviceUnavailable: { type: "final" },
    },
}, {
    actions: {
        showSuccessMessage: (context, event) => {
            toaster.ShowSuccess(event.data.successMessages[0]);
        },
        openPdfBlob: (context, event) => {
            const { blob, fileName } = event.data;
            if (blob) {
                const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = fileName;
                a.click();
                window.URL.revokeObjectURL(url);
            }
        },
        openPdfInNewTab: (context, event) => {
            const { blob, fileName } = event.data;
            if (blob) {
                const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
                const newWindow = window.open(url, "_blank");
                if (newWindow) {
                    setTimeout(() => {
                        try {
                            newWindow.document.title = fileName;
                        }
                        catch (e) { }
                    }, 500);
                }
            }
        },
        clearSelectedAssetIds: assign({
            selectedAssetIds: () => [],
        }),
        assignSelectedAssetIds: assign({
            selectedAssetIds: (context, event) => event.assetIds,
        }),
        clearReturnLetterAssets: assign({
            returnLetterAssets: () => [],
        }),
        assignReturnLetterAssets: assign({
            returnLetterAssets: (context, event) => event.assets,
        }),
        assignDataToSearch: assign({
            query: (context, event) => event.str,
        }),
        assignEmployeeId: assign({
            empId: (context, event) => event.employeeId,
        }),
    },
    guards: {},
    activities: {},
    services: {},
});
export default assetAssignmentMachine;
